import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { approvalQueue as seedApprovals, auditTrail as seedAudit, backgroundJobs, configSettings as seedConfig, integrations, webhookEvents, currentStaffHandle, invitations as seedInvitations, launchChecks, permissionRegister, roleRegister, safetySwitches as seedSwitches, staffDirectory, tenants as seedTenants } from '../data/governance';
import { can, canApprove, describeScope, type Decision } from '../utils/governance';
import type { ApprovalRequest, AuditRecord, ConfigSetting, Permission, RoleRecord, SafetySwitch, Scope, StaffInvitation, StaffMember, Tenant } from '../types/governance';
interface GovernanceValue {
  tenants: Tenant[];
  roles: RoleRecord[];
  permissions: typeof permissionRegister;
  staff: StaffMember[];
  invitations: StaffInvitation[];
  approvals: ApprovalRequest[];
  config: ConfigSetting[];
  switches: SafetySwitch[];
  audit: AuditRecord[];
  launchChecks: typeof launchChecks;
  integrations: typeof integrations;
  webhooks: typeof webhookEvents;
  jobs: typeof backgroundJobs;

  /** The signed-in administrator. */
  me: StaffMember;
  /** The scope the console is currently looking at. */
  activeScope: Scope;
  setActiveScope: (scope: Scope) => void;
  scopeLabel: (scope: Scope) => string;

  /** The only authorization question any surface asks. */
  allows: (permission: Permission, scope?: Scope) => Decision;
  approvalDecision: (request: ApprovalRequest) => Decision;
  invite: (input: {
    name: string;
    email: string;
    roleId: string;
    scope: Scope;
  }) => void;
  revokeInvitation: (invitationId: string) => void;
  assignRole: (input: {
    userId: string;
    roleId: string;
    scope: Scope;
    reason: string;
    expiresAt: string | null;
  }) => void;
  revokeAssignment: (userId: string, assignmentId: string, reason: string) => void;
  reviewAccess: (userId: string) => void;
  decideApproval: (requestId: string, approve: boolean, reason: string) => void;
  setSwitch: (switchId: string, state: 'enabled' | 'paused', reason: string) => void;
  /** Records a denied attempt, so refusals are as auditable as successes. */
  recordAttempt: (input: {
    action: string;
    resource: string;
    resourceId: string;
    reason: string;
  }) => void;
  /** Records a completed administrative action against the same append-only log. */
  logAction: (input: {
    action: string;
    resource: string;
    resourceId: string;
    before?: string | null;
    after?: string | null;
    reason: string;
  }) => void;
}
const Ctx = createContext<GovernanceValue | null>(null);
export function GovernanceProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [tenants] = useState<Tenant[]>(seedTenants);
  const [roles] = useState<RoleRecord[]>(roleRegister);
  const [staff, setStaff] = useState<StaffMember[]>(staffDirectory);
  const [invitations, setInvitations] = useState<StaffInvitation[]>(seedInvitations);
  const [approvals, setApprovals] = useState<ApprovalRequest[]>(seedApprovals);
  const [config] = useState<ConfigSetting[]>(seedConfig);
  const [switches, setSwitches] = useState<SafetySwitch[]>(seedSwitches);
  const [audit, setAudit] = useState<AuditRecord[]>(seedAudit);
  const [activeScope, setActiveScope] = useState<Scope>({
    level: 'global'
  });
  const me = useMemo(() => staff.find(member => member.handle === currentStaffHandle) ?? staff[0], [staff]);
  const myRoleName = useMemo(() => {
    const first = me.assignments.find(assignment => assignment.state === 'active');
    return roles.find(role => role.id === first?.roleId)?.name ?? 'Administrator';
  }, [me, roles]);
  const scopeLabel = useCallback((scope: Scope) => describeScope(scope, tenants), [tenants]);

  /** Append-only. Nothing in this context can remove or rewrite a record. */
  const log = useCallback((entry: Omit<AuditRecord, 'id' | 'at' | 'actor' | 'actorRole' | 'scope'>) => {
    setAudit(current => [{
      id: `aud-${Date.now()}`,
      at: new Date().toISOString(),
      actor: me.handle,
      actorRole: myRoleName,
      scope: describeScope(activeScope, tenants),
      ...entry
    }, ...current]);
  }, [activeScope, me.handle, myRoleName, tenants]);
  const allows = useCallback<GovernanceValue['allows']>((permission, scope) => can(me, roles, permission, scope ?? activeScope), [activeScope, me, roles]);
  const approvalDecision = useCallback<GovernanceValue['approvalDecision']>(request => canApprove(me, roles, request), [me, roles]);
  const invite = useCallback<GovernanceValue['invite']>(input => {
    const now = new Date();
    const expires = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const invitation: StaffInvitation = {
      id: `inv-${Date.now()}`,
      email: input.email,
      name: input.name,
      roleId: input.roleId,
      scope: input.scope,
      invitedBy: me.handle,
      invitedAt: now.toISOString(),
      expiresAt: expires.toISOString(),
      status: 'pending',
      tokenConsumed: false
    };
    setInvitations(current => [invitation, ...current]);
    log({
      action: 'Sent staff invitation',
      resource: 'Invitation',
      resourceId: invitation.id,
      before: null,
      after: `${roles.find(role => role.id === input.roleId)?.name} — ${describeScope(input.scope, tenants)}`,
      reason: 'Administrative staffing.',
      result: 'succeeded'
    });
  }, [log, me.handle, roles, tenants]);
  const revokeInvitation = useCallback<GovernanceValue['revokeInvitation']>(invitationId => {
    setInvitations(current => current.map(invitation => invitation.id === invitationId ? {
      ...invitation,
      status: 'revoked'
    } : invitation));
    log({
      action: 'Revoked invitation',
      resource: 'Invitation',
      resourceId: invitationId,
      before: 'Pending',
      after: 'Revoked — token invalidated',
      reason: 'Invitation withdrawn.',
      result: 'succeeded'
    });
  }, [log]);
  const assignRole = useCallback<GovernanceValue['assignRole']>(input => {
    const role = roles.find(item => item.id === input.roleId);
    if (!role) return;
    const privileged = Boolean(role.privileged);
    const assignmentId = `asg-${Date.now()}`;
    setStaff(current => current.map(member => member.userId === input.userId ? {
      ...member,
      assignments: [...member.assignments, {
        id: assignmentId,
        roleId: input.roleId,
        scope: input.scope,
        assignedBy: me.handle,
        assignedAt: new Date().toISOString(),
        expiresAt: input.expiresAt,
        state: privileged ? 'awaiting_approval' : 'active'
      }]
    } : member));
    if (privileged) {
      setApprovals(current => [{
        id: `apr-${Date.now()}`,
        kind: 'role_assignment',
        summary: `${role.name} — ${describeScope(input.scope, tenants)}`,
        requestedBy: me.handle,
        requestedAt: new Date().toISOString(),
        scope: input.scope,
        state: 'requested',
        reason: input.reason,
        decidedBy: null,
        decidedAt: null,
        decisionReason: null,
        permissionToApprove: 'staff.approve'
      }, ...current]);
    }
    log({
      action: 'Assigned role',
      resource: 'Role assignment',
      resourceId: assignmentId,
      before: 'No such assignment',
      after: `${role.name} — ${describeScope(input.scope, tenants)}${privileged ? ' (awaiting approval)' : ''}`,
      reason: input.reason,
      result: 'succeeded'
    });
  }, [log, me.handle, roles, tenants]);
  const revokeAssignment = useCallback<GovernanceValue['revokeAssignment']>((userId, assignmentId, reason) => {
    setStaff(current => current.map(member => member.userId === userId ? {
      ...member,
      assignments: member.assignments.map(assignment => assignment.id === assignmentId ? {
        ...assignment,
        state: 'revoked' as const
      } : assignment)
    } : member));
    log({
      action: 'Revoked role assignment',
      resource: 'Role assignment',
      resourceId: assignmentId,
      before: 'Active',
      after: 'Revoked — access stopped immediately',
      reason,
      result: 'succeeded'
    });
  }, [log]);
  const reviewAccess = useCallback<GovernanceValue['reviewAccess']>(userId => {
    const at = new Date().toISOString();
    setStaff(current => current.map(member => member.userId === userId ? {
      ...member,
      lastReviewedAt: at
    } : member));
    log({
      action: 'Completed access review',
      resource: 'Staff member',
      resourceId: userId,
      before: null,
      after: `Reviewed ${at}`,
      reason: 'Periodic administrative access review.',
      result: 'succeeded'
    });
  }, [log]);
  const decideApproval = useCallback<GovernanceValue['decideApproval']>((requestId, approve, reason) => {
    const request = approvals.find(item => item.id === requestId);
    if (!request) return;
    setApprovals(current => current.map(item => item.id === requestId ? {
      ...item,
      state: approve ? 'approved' : 'rejected',
      decidedBy: me.handle,
      decidedAt: new Date().toISOString(),
      decisionReason: reason
    } : item));

    // An approved role assignment is what actually activates the assignment.
    if (approve && request.kind === 'role_assignment') {
      setStaff(current => current.map(member => ({
        ...member,
        assignments: member.assignments.map(assignment => assignment.state === 'awaiting_approval' ? {
          ...assignment,
          state: 'active' as const,
          approvedBy: me.handle
        } : assignment)
      })));
    }
    log({
      action: approve ? 'Approved request' : 'Rejected request',
      resource: 'Approval request',
      resourceId: requestId,
      before: 'Requested',
      after: approve ? 'Approved' : 'Rejected',
      reason,
      result: 'succeeded'
    });
  }, [approvals, log, me.handle]);
  const setSwitch = useCallback<GovernanceValue['setSwitch']>((switchId, state, reason) => {
    const target = switches.find(item => item.id === switchId);
    if (!target || target.state === 'unavailable') return;
    setSwitches(current => current.map(item => item.id === switchId ? {
      ...item,
      state,
      lastChangedBy: me.handle,
      lastChangedAt: new Date().toISOString()
    } : item));
    log({
      action: state === 'paused' ? 'Paused capability' : 'Resumed capability',
      resource: 'Safety switch',
      resourceId: switchId,
      before: target.state,
      after: state,
      reason,
      result: 'succeeded'
    });
  }, [log, me.handle, switches]);
  const recordAttempt = useCallback<GovernanceValue['recordAttempt']>(input => {
    log({
      action: input.action,
      resource: input.resource,
      resourceId: input.resourceId,
      before: null,
      after: null,
      reason: input.reason,
      result: 'denied'
    });
  }, [log]);
  const logAction = useCallback<GovernanceValue['logAction']>(input => {
    log({
      action: input.action,
      resource: input.resource,
      resourceId: input.resourceId,
      before: input.before ?? null,
      after: input.after ?? null,
      reason: input.reason,
      result: 'succeeded'
    });
  }, [log]);
  const value = useMemo<GovernanceValue>(() => ({
    tenants,
    roles,
    permissions: permissionRegister,
    staff,
    invitations,
    approvals,
    config,
    switches,
    audit,
    launchChecks,
    integrations,
    webhooks: webhookEvents,
    jobs: backgroundJobs,
    me,
    activeScope,
    setActiveScope,
    scopeLabel,
    allows,
    approvalDecision,
    invite,
    revokeInvitation,
    assignRole,
    revokeAssignment,
    reviewAccess,
    decideApproval,
    setSwitch,
    recordAttempt,
    logAction
  }), [tenants, roles, staff, invitations, approvals, config, switches, audit, me, activeScope, scopeLabel, allows, approvalDecision, invite, revokeInvitation, assignRole, revokeAssignment, reviewAccess, decideApproval, setSwitch, recordAttempt, logAction]);
  return <Ctx.Provider value={value} data-id="element-6057" data-mp="y7j7xm8qrfn6">{children}</Ctx.Provider>;
}
export function useGovernance(): GovernanceValue {
  const value = useContext(Ctx);
  if (!value) throw new Error('useGovernance must be used inside GovernanceProvider');
  return value;
}